import type { ReactNode } from "react";
import { PricingPlan, type PricingPlanGb } from "services/pricingPlan/pricingPlanTypes";

export interface PricingPlanFeature {
  title: string;
  description: ReactNode;
  pricingPlans: PricingPlanGb[];
  link?: { text: string; href: string };
}

export type PricingPlanFeatures = PricingPlanFeature[];

export const PRICING_PLAN_FEATURES: PricingPlanFeatures = [
  {
    title: "Free company registration",
    description: "£100 Companies House fee and ID verification with personal code (if needed) are covered",
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.BlackFriday2025,
    ],
  },
  {
    title: "Business account",
    description: (
      <>
        A Pay-As-You-Go ANNA business account
        <br />
        <br />
        <br />
      </>
    ),
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.JustRegister,
      PricingPlan.JustRegister2026,
      PricingPlan.LaunchPack,
      PricingPlan.Privacy,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Basic admin",
    description: "Easy receipt capture, instant invoicing, XLS VAT filing and accounting software sync-up",
    pricingPlans: [
      PricingPlan.JustRegister,
      PricingPlan.JustRegister2026,
      PricingPlan.LaunchPack,
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.Privacy,
      PricingPlan.AllInOne,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Virtual office",
    description: (
      <>
        Keep your address private and get same-day mail scanning to your inbox <b>worth £180/year</b>
      </>
    ),
    pricingPlans: [
      PricingPlan.BusinessSecretary,
      PricingPlan.Privacy,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      // Drives the virtual-office question for the new-pricing "Secretary" plan.
      PricingPlan.SecretaryYearly2026,
    ],
  },
  {
    title: "+Taxes",
    description: (
      <>
        Year-end Annual Accounts, Corporation Tax filing, and Payroll setup for one employee or director,{" "}
        <b>worth £288/year</b>
      </>
    ),
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.BusinessEssentials,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
    ],
    link: { text: "Find out more", href: "https://anna.money/plus-taxes/" },
  },
  {
    title: "VAT registration",
    description: "We'll set up VAT Registration on your behalf upon your request",
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "PAYE registration",
    description: "Registration of your company as an employer with HMRC upon your request",
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Confirmation statement",
    description: "Avoid late fees! Get reminders and file your confirmation statement directly with the ANNA app",
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Dormant account filing",
    description: "Simplified compliance and filing for inactive companies, hassle-free",
    pricingPlans: [
      PricingPlan.BusinessEssentials,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Company details management",
    description: "Registered office address, SIC code update, company name change (Companies House fees apply)",
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Cancellation cover",
    description: "Peace of mind in case you need to close your company for any reason",
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.BusinessSecretary,
      PricingPlan.BusinessEssentials,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },

  {
    title: "Shares management",
    description:
      "Issue and transfer of shares, documentation management and record updates to stay compliant with Companies House",
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
  {
    title: "Officer management",
    description: "Officer or shareholder appointments and changes on demand, all with full legal compliance",
    pricingPlans: [
      PricingPlan.FullFormation19,
      PricingPlan.BusinessSecretary,
      PricingPlan.AllInOne,
      PricingPlan.SecretaryMonthly2025,
      PricingPlan.SecretaryYearly2025,
      PricingPlan.SecretaryPlusMonthly2025,
      PricingPlan.SecretaryPlusYearly2025,
      PricingPlan.BusinessSupportYearly2026,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
      PricingPlan.BlackFriday2025,
      PricingPlan.BusinessLaunch2026,
    ],
  },
];

// We only want to change the description of existing feature, everything else is the same
export const ALL_IN_ONE_FEATURES: PricingPlanFeatures = [
  {
    title: "Free company registration",
    description: "£100 Companies House fee and ID verification with personal code (if needed) are covered",
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
    ],
  },
  {
    title: "Business account",
    description: (
      <>
        Business account with free local transfers, savings pots, cash deposits, and international payments{" "}
        <b>worth £219/year</b>
      </>
    ),
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
    ],
  },
  ...PRICING_PLAN_FEATURES.slice(2, 12),
  {
    title: "Officer management",
    description: "Director or shareholder appointments and changes on demand, all with full legal compliance",
    pricingPlans: [
      PricingPlan.AllInOne,
      PricingPlan.TotalSupportMonthly2025,
      PricingPlan.TotalSupportYearly2025,
      PricingPlan.TotalSupportYearly2026,
    ],
  },
];

export const BUSINESS_LAUNCH_FEATURES: PricingPlanFeatures = [
  {
    title: "Company registration",
    description:
      "Pay standard £100 Companies House fee for company registration with ID verification and personal code (if needed)",
    pricingPlans: [PricingPlan.BusinessLaunch2026],
  },
  {
    title: "Business account",
    description: (
      <>
        A Pay-As-You-Go ANNA business account
        <br />
        <br />
        <br />
      </>
    ),
    pricingPlans: [PricingPlan.BusinessLaunch2026],
  },
  ...PRICING_PLAN_FEATURES.slice(2, 7),
  {
    title: "Confirmation statement",
    description: (
      <>
        File your first confirmation statement directly with the ANNA app
        <br />
        <br />
      </>
    ),
    pricingPlans: [PricingPlan.BusinessLaunch2026],
  },
  ...PRICING_PLAN_FEATURES.slice(8),
];

export const LAUNCHPACK_FEATURES: PricingPlanFeatures = [
  {
    title: "The £25 cashback",
    description: "Get £25 credited when you top up your business account",
    pricingPlans: [PricingPlan.LaunchPack],
  },
  ...PRICING_PLAN_FEATURES.slice(1),
];

export const BLACK_FRIDAY_FEATURES: PricingPlanFeatures = [
  {
    title: "Free company registration with ACSP service",
    description: "The £100 registration fee and identity verification are included",
    pricingPlans: [PricingPlan.BlackFriday2025],
  },
  {
    title: "Business account",
    description: (
      <>
        Business plan of ANNA business account <b>worth £149/year</b>
      </>
    ),
    pricingPlans: [PricingPlan.BlackFriday2025],
  },
  ...PRICING_PLAN_FEATURES.slice(2),
];

// Card order of the new-pricing lineup — single source for NewPricingPlanCards and checkout.
export const NEW_PRICING_PLANS: PricingPlanGb[] = [
  PricingPlan.JustRegister2026,
  PricingPlan.SecretaryLite2026,
  PricingPlan.SecretaryYearly2026,
  PricingPlan.TotalSupportYearly2026,
];

const NEW_PRICING_SECRETARY_PLANS: PricingPlanGb[] = [
  PricingPlan.SecretaryLite2026,
  PricingPlan.SecretaryYearly2026,
  PricingPlan.TotalSupportYearly2026,
];

const BUSINESS_ACCOUNT_EXTENDED_DESCRIPTION =
  "Includes free local transfers, saving pots, international payments and more";

const NEW_PRICING_BENEFITS: PricingPlanFeatures = [
  {
    title: "Business account",
    description: "Choose the account from flexible pay-as-you-go to unlimited transfers",
    pricingPlans: NEW_PRICING_PLANS,
  },
  {
    title: "Basic admin",
    description: "Easy receipt capture, invoicing, xls VAT filing and accounting software sync up",
    pricingPlans: NEW_PRICING_PLANS,
  },
  {
    title: "Virtual office",
    description: "Keep your address private and get same day mail scanning to your inbox",
    // Must stay consistent with the "Virtual office" feature in PRICING_PLAN_FEATURES,
    // which drives the virtual-office question in the basket flow.
    pricingPlans: [PricingPlan.SecretaryYearly2026, PricingPlan.TotalSupportYearly2026],
  },
  {
    title: "VAT registration",
    description: "VAT Registration on your behalf upon your request",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "PAYE registration",
    description: "Registration as an employer with HMRC upon your request",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "Change of company name or other details",
    description: "Registered office address, SIC code update, company name change (Companies House fees apply)",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "Director and share management services",
    description:
      "Add or remove director, issue or transfer of shares plus record updates to stay compliant with Companies House",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "Confirmation statement",
    description: "Get reminders and file your confirmation statement directly with the ANNA app",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "Company cancellation cover",
    description: "Peace of mind in case you need to close your company for any reason",
    pricingPlans: NEW_PRICING_SECRETARY_PLANS,
  },
  {
    title: "+Taxes",
    description: "All your taxes covered (Payroll, VAT filing and Corporation Tax)",
    pricingPlans: [PricingPlan.TotalSupportYearly2026],
  },
];

const NEW_PRICING_BENEFITS_OVERRIDES: Partial<Record<PricingPlanGb, Record<string, ReactNode>>> = {
  [PricingPlan.JustRegister2026]: {
    "Confirmation statement":
      "Avoid late fees! Get reminders and file your confirmation statement directly with the ANNA app",
  },
  [PricingPlan.SecretaryLite2026]: {
    "Confirmation statement": "First confirmation statement is on us - worth £79/filing",
  },
  [PricingPlan.TotalSupportYearly2026]: {
    "Business account": BUSINESS_ACCOUNT_EXTENDED_DESCRIPTION,
  },
};

const applyBenefitOverrides = (overrides?: Record<string, ReactNode>): PricingPlanFeatures => {
  if (!overrides) {
    return NEW_PRICING_BENEFITS;
  }
  return NEW_PRICING_BENEFITS.map((feature) =>
    overrides[feature.title] ? { ...feature, description: overrides[feature.title] } : feature,
  );
};

export const getNewPricingBenefits = (pricingPlan: PricingPlanGb): PricingPlanFeatures =>
  applyBenefitOverrides(NEW_PRICING_BENEFITS_OVERRIDES[pricingPlan]);

// Just Register benefits under formationsJustRegisterIncludesBusinessAccount
export const JUST_REGISTER_INCLUDES_BUSINESS_ACCOUNT_BENEFITS = applyBenefitOverrides({
  ...NEW_PRICING_BENEFITS_OVERRIDES[PricingPlan.JustRegister2026],
  "Business account": BUSINESS_ACCOUNT_EXTENDED_DESCRIPTION,
});

export const JUST_REGISTER_2026_FEATURES: PricingPlanFeatures = [
  {
    title: "Company registration",
    description:
      "Limited company for only £19+VAT instead of £100. Plus ID verification and personal code (if needed).",
    pricingPlans: [PricingPlan.JustRegister2026],
  },
  ...PRICING_PLAN_FEATURES.slice(1),
];
